import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import tokenManager from '../utils/tokenManager';
import config from '../config';
import apiClient from '../utils/axiosInterceptor';

interface AuthCallbackProps {
    onAuthSuccess: () => void;
    onUserUpdate: () => Promise<void>;
}

function getTokensFromUrl(): { accessToken: string | null; refreshToken: string | null } {
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const queryParams = new URLSearchParams(window.location.search);

    return {
        accessToken: hashParams.get('accessToken') || queryParams.get('accessToken'),
        refreshToken: hashParams.get('refreshToken') || queryParams.get('refreshToken'),
    };
}

const AuthCallback = ({ onAuthSuccess, onUserUpdate }: AuthCallbackProps) => {
    const navigate = useNavigate();
    const [status, setStatus] = useState('Logging you in...');
    const handledRef = useRef(false);

    useEffect(() => {
        if (handledRef.current) {
            return;
        }
        handledRef.current = true;

        const handleAuthCallback = async () => {
            const { accessToken, refreshToken } = getTokensFromUrl();

            if (!accessToken || !refreshToken) {
                console.error('Auth callback missing tokens in URL');
                navigate('/login');
                return;
            }

            tokenManager.storeTokens(accessToken, refreshToken);
            window.history.replaceState(null, '', '/auth-callback');

            try {
                setStatus('Fetching your profile...');

                const res = await apiClient.get(`${config.backendUrl}users/me`);
                const user = res.data;

                onAuthSuccess();
                await onUserUpdate();

                const isSubscribed = Boolean(user.subscription && user.subscription !== 'none');
                const hasNewsletterInfo = user.newsletterInfo && user.newsletterInfo.topic;
                const isGoogleSignup = Boolean(user.googleId);

                if (isSubscribed) {
                    setStatus('Redirecting to sponsors...');
                    window.location.href = '/sponsors';
                } else if (isGoogleSignup && !hasNewsletterInfo) {
                    setStatus('Setting up your account...');
                    window.location.href = '/newsletter-opt-in';
                } else if (!hasNewsletterInfo) {
                    setStatus('Setting up your newsletter profile...');
                    window.location.href = '/onboarding';
                } else {
                    // Onboarded but not subscribed — land on the (locked
                    // preview) database rather than an immediate subscribe
                    // pitch, same as the email-signup path.
                    setStatus('Redirecting to sponsors...');
                    window.location.href = '/sponsors';
                }
            } catch (error) {
                console.error('Error fetching user info after Google login:', error);
                tokenManager.clearTokens();
                navigate('/login?error=auth_callback_failed');
            }
        };

        handleAuthCallback();
    }, [navigate, onAuthSuccess, onUserUpdate]);

    return (
        <div className="web-page">
            <div className="login-container">
                <div className="login-form">
                    <h1>{status}</h1>
                    <div style={{ marginTop: '20px', textAlign: 'center' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuthCallback;
